import React from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector, useDispatch } from 'react-redux';
import { useScrollIntoView } from 'components/AssetList/hooks';
import ImageBox from 'components/ImageBox';
import { ModalType } from 'const';
import { addSelectedAssetPanelComponent, removeSelectedAssetPanelComponent } from 'containers/App/actions';
import { selectedComponentItem } from 'containers/App/selectors';
import { deleteComponent } from 'containers/Documents/actions';
import { showModal } from 'containers/ModalManager/actions';
import { documentIdsAreBeingUploaded as documentIdsAreBeingUploadedSelector } from 'containers/Project/selectors';
import { setAssetIdToScroll as setAssetIdToScrollAction } from 'containers/ProjectPanel/actions';
import * as ProjectPanelModels from 'containers/ProjectPanel/models';
import { assetIdToScroll as assetIdToScrollSelector, selectComponentCount } from 'containers/ProjectPanel/selectors';
import { Priority, useClickOutside } from 'hooks/useClickOutside';
import { Image, ImageMap } from 'models';
import styles from './styles.module.scss';
import { useImageListItemDragSource } from './useImageListItemDragSource';

type Props = {
  allowChange: boolean;
  allowDelete: boolean;
  component: ImageMap;
  dataTestId?: string;
};

const ImageListItem: React.FunctionComponent<Props> = (props) => {
  const {
    allowChange,
    allowDelete,
    component,
    dataTestId = 'imageListItem',
  } = props;
  const dispatch = useDispatch();
  const componentId = component.get('id');
  const assetIdToScroll = useSelector(assetIdToScrollSelector);
  const imageCount = useSelector(selectComponentCount(componentId));
  const isSelected = useSelector(selectedComponentItem(componentId));
  const documentIdsAreBeingUploaded = useSelector(documentIdsAreBeingUploadedSelector);
  const setAssetIdToScroll: ProjectPanelModels.ActionCreator.SetAssetIdToScroll = (assetId) => {
    return dispatch(setAssetIdToScrollAction(assetId));
  };
  const { elementRef } = useScrollIntoView(componentId, assetIdToScroll, setAssetIdToScroll);

  const isImageBeingUploaded = documentIdsAreBeingUploaded.has(componentId);
  const internalImageInfo = component.get('_internalInfo');
  const imageSource = internalImageInfo?.get('source');

  const { isDragging, dragRef, preview } = useImageListItemDragSource({
    component,
    isImageBeingUploaded,
    imageSource,
  });
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const { container } = useClickOutside<HTMLDivElement>(Priority.SELECTION, {
    active: isSelected,
    onClickOutside: () => dispatch(removeSelectedAssetPanelComponent(componentId)),
  });

  const showImageDetails = (): void => {
    dispatch(showModal(ModalType.IMAGE_DETAILS, { document: component.toJS() as Image, allowChange }));
  };

  const deleteImage = (): void => {
    const entityType = component.get('entityType');
    dispatch(deleteComponent(componentId, entityType));
  };

  const onSelectHandler = (): void => {
    dispatch(addSelectedAssetPanelComponent(componentId));
  };

  return dragRef(
    <div className={styles.ImageListItem}
      ref={elementRef} onMouseDown={allowChange ? onSelectHandler : (): void => {}}
      data-testid={dataTestId}
    >
      <ImageBox
        container={container}
        deleteImage={allowDelete ? deleteImage : undefined}
        image={component}
        imageCount={allowDelete ? imageCount : undefined}
        isDragging={isDragging}
        isImageBeingUploaded={isImageBeingUploaded}
        isSelected={allowChange && isSelected}
        showImageDetails={showImageDetails}
        dataTestId={`${dataTestId}-imageBox`}
      />
    </div>,
  );
};

export default ImageListItem;
